// lib/worker-rpc.ts

export interface WirePixels {
  buffer: ArrayBuffer;
  width: number;
  height: number;
}

type WorkerResponse<Result> = { messageId: string; error: string } | { messageId: string; result: Result };

/** Marks a worker that is dead (silent or crashed), as opposed to one job whose computation threw. */
const WORKER_UNAVAILABLE_ERROR_NAME = 'WorkerUnavailable';

const workerUnavailableError = (message: string) => Object.assign(new Error(message), { name: WORKER_UNAVAILABLE_ERROR_NAME });

const isWorkerUnavailableError = (error: unknown) => error instanceof Error && error.name === WORKER_UNAVAILABLE_ERROR_NAME;

export const copyPixels = (image: ImageData): WirePixels => ({
  buffer: new Uint8ClampedArray(image.data).buffer as ArrayBuffer,
  width: image.width,
  height: image.height,
});

export const toImageData = (pixels: WirePixels) => new ImageData(new Uint8ClampedArray(pixels.buffer), pixels.width, pixels.height);

/** Decodes a worker reply, rejecting a buffer that does not match its declared size. */
export const decodeWorkerPixels = (label: string, pixels: WirePixels): ImageData => {
  const { width, height } = pixels;
  const data = new Uint8ClampedArray(pixels.buffer);
  if (data.length !== width * height * 4) {
    throw new Error(`${label} worker returned invalid buffer length (${data.length}, expected ${width * height * 4}).`);
  }
  return new ImageData(data, width, height);
};

interface WorkerRequestOptions {
  /** Capitalised job name, e.g. 'Layer raster', prefixed to every error message. */
  label: string;
  /** Bounds worker silence, not job duration: any message re-arms it. Absent means no timer at all. */
  timeoutMs?: number;
  /** Whether a worker `error` event marks the worker unavailable or is a plain Error. */
  errorEventMarksUnavailable: boolean;
}

export const sendWorkerRequest = <Payload extends object, Result>(
  worker: Worker,
  payload: Payload,
  transfer: Transferable[],
  { label, timeoutMs, errorEventMarksUnavailable }: WorkerRequestOptions,
): Promise<Result> => {
  const messageId = `worker-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const unknownError = `Unknown ${label.toLowerCase()} worker error.`;

  return new Promise<Result>((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onSilence = () => {
      detach();
      reject(workerUnavailableError(`${label} worker did not answer within ${timeoutMs} ms.`));
    };
    const armTimer = () => {
      clearTimeout(timer);
      if (timeoutMs !== undefined) timer = setTimeout(onSilence, timeoutMs);
    };
    const detach = () => {
      clearTimeout(timer);
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
    };
    const onMessage = (event: MessageEvent<WorkerResponse<Result>>) => {
      armTimer();
      if (!event.data || event.data.messageId !== messageId) return;
      detach();
      const response = event.data;
      if ('error' in response) {
        reject(new Error(response.error));
        return;
      }
      resolve(response.result);
    };
    const onError = (errorEvent: ErrorEvent) => {
      detach();
      const message = errorEvent.message || unknownError;
      reject(errorEventMarksUnavailable ? workerUnavailableError(message) : new Error(message));
    };

    armTimer();
    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onError);
    try {
      worker.postMessage({ messageId, ...payload }, transfer);
    } catch (error) {
      detach();
      reject(error instanceof Error ? error : new Error(`Failed to post ${label.toLowerCase()} message to worker.`));
    }
  });
};

/**
 * Worker first, main thread on any failure. A dead worker is terminated and dropped from `workerRef`
 * so later jobs skip it; a job whose computation threw keeps the worker.
 */
export const runWithWorkerFallback = async <Result>(
  workerRef: { current: Worker | null },
  runInWorker: (worker: Worker) => Promise<Result>,
  runOnMain: () => Result | PromiseLike<Result>,
  failureWarning: string,
): Promise<{ result: Result; executionPath: 'worker' | 'worker->fallback-main' | 'main' }> => {
  const worker = workerRef.current;
  if (!worker) return { result: await runOnMain(), executionPath: 'main' };
  try {
    return { result: await runInWorker(worker), executionPath: 'worker' };
  } catch (error) {
    console.warn(failureWarning, error);
    if (isWorkerUnavailableError(error) && workerRef.current === worker) {
      worker.terminate();
      workerRef.current = null;
    }
    return { result: await runOnMain(), executionPath: 'worker->fallback-main' };
  }
};
